import { parse } from "yaml";
import { setupHeaderScrollState } from "../../../../../Brand/Web/ts/header";
import { buildHeadingToc, observeTableWraps, parseMarkdown, sanitizeHtml } from "../../../../../Brand/Web/ts/markdown";
import { setupAssistant } from "./assistant";
import { isCcnlHref, parseCcnlLink } from "./ccnl";
import { buildVersionOrder, resolveTargetFile as resolveFileAt, visibleFiles, } from "./revisions";
import { buildSnippet, createSearchEngine, parseSearchIndex, splitByTerms, } from "./search";
const productIdPattern = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;
const versionIdPattern = /^[a-z0-9][a-z0-9.-]*$/;
const pageIdPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
// content/pages/ nests to any depth (see ../../data-store.md) — one or more
// category folders, then the page's own <id>/<version>.md.
const revisionFilePattern = /^pages\/(?:[a-z0-9]+(?:-[a-z0-9]+)*\/)+[a-z0-9][a-z0-9.-]*\.md$/;
const changelogTemplate = "changelogs/{version}.md";
// Content Markdown may start with a YAML front matter block (title/tags/
// keywords/source, or versionFile) — see ../../data-store.md. It is metadata, not
// content, so it is stripped before the Markdown renderer ever sees the text.
const frontMatterPattern = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;
const logicalPageLinkPattern = /^\?page=([a-z0-9]+(?:-[a-z0-9]+)*)(?:&[^#]*)?(?:#.*)?$/;
// A hivatkozó menüpont "link" értéke — ugyanezek a minták a
// build-pages-yaml.mjs-ben és a validate-data.mjs-ben.
const menuPageLinkPattern = /^\?page=([a-z0-9]+(?:-[a-z0-9]+)*)$/;
const menuExternalLinkPattern = /^https:\/\/\S+$/;
const storageKey = "cobra-docs-version";
function getElement(id) {
    const element = document.getElementById(id);
    if (!element) {
        throw new Error(`Hiányzó felületelem: #${id}`);
    }
    return element;
}
const productSelect = getElement("product-select");
const versionSelect = getElement("version-select");
const searchInput = getElement("search-input");
const clearSearchButton = getElement("clear-search");
const searchResults = getElement("search-results");
const searchButton = getElement("search-button");
const contentsTree = getElement("contents-tree");
const treeEmpty = getElement("tree-empty");
const article = getElement("document-content");
const tocPanel = getElement("page-toc-panel");
const pageToc = getElement("page-toc");
const status = getElement("status");
const sidebar = getElement("sidebar");
const menuButton = getElement("menu-button");
const menuCloseButton = getElement("menu-close");
const drawerBackdrop = getElement("drawer-backdrop");
const navToggle = getElement("navToggle");
const navPanel = getElement("navPanel");
const siteNavList = getElement("site-nav-list");
const heroTitle = getElement("hero-title");
const heroLead = getElement("hero-lead");
// A compact hero címsora (index.html #hero-title) az aktuálisan betöltött
// dokumentum tényleges címét mutatja, nem egy statikus szöveget — ezért a
// document.title beállításával párhuzamosan mindenhol ezt is frissíteni
// kell (lásd renderMarkdown / showArticlePrompt / showArticleError). Ha
// nincs cím (pl. még nem töltődött be dokumentum), a kiválasztott termék
// baseTitle()-je jelenik meg — ugyanaz a fallback, mint a document.title-nél.
function setHeroTitle(text) {
    heroTitle.textContent = text;
}
function productLabel() {
    return state.products.find((product) => product.id === state.product)?.label ?? "";
}
// "<termék> Tudásbázis" — a lap címe és a hero alapértelmezett címsora.
function baseTitle() {
    return `${productLabel()} Tudásbázis`;
}
// A kiválasztott termék tartalommappája a kimenetben (content/<termék>/): ide
// képezi le a termékhez tartozó fájlok útját (versions.yaml, pages.yaml, a
// revíziók, a keresési index).
function contentUrl(path) {
    return `../content/${encodeURIComponent(state.product ?? "")}/${path}`;
}
const state = {
    products: [],
    versions: [],
    versionOrder: new Map(),
    items: [],
    pages: new Map(),
    expanded: new Set(),
    search: "",
};
let requestController;
let tocController;
// A súgó-asszisztens (./assistant.ts) — csak ha a build bekapcsolta (a sablonban van
// #assistant elem, lásd webpack.config.js "assistantEnabled"), és a háttere elérhető.
let assistant;
// Termékenként: a menüfa; a letöltött és a renderelt oldalak kulcsa a
// tartalommappán belüli út a termékkel együtt ("conto.net/pages/…").
const cachedContents = new Map();
const pageCache = new Map();
const renderedPageCache = new Map();
function createLoadProfiler(version, page) {
    const started = performance.now();
    let previous = started;
    const prefix = `[Cobra Docs] [${version}/${page}]`;
    return {
        step(message, details) {
            const now = performance.now();
            const timing = `+${(now - started).toFixed(1)} ms, delta ${(now - previous).toFixed(1)} ms`;
            previous = now;
            if (details) {
                console.info(`${prefix} [${new Date().toISOString()}] [${timing}] ${message}`, details);
            }
            else {
                console.info(`${prefix} [${new Date().toISOString()}] [${timing}] ${message}`);
            }
        },
    };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
// Ugyanazok a szabályok, mint a build oldalán (src/help-products.mjs).
function validateProducts(value) {
    if (!isRecord(value) || !Array.isArray(value.products) || value.products.length === 0) {
        throw new Error("A products.yaml nem tartalmaz érvényes terméklistát.");
    }
    const ids = new Set();
    const products = value.products.map((entry) => {
        if (!isRecord(entry) || typeof entry.id !== "string" || !productIdPattern.test(entry.id) || ids.has(entry.id)) {
            throw new Error("Érvénytelen vagy ismétlődő termékazonosító a products.yaml fájlban.");
        }
        if (typeof entry.label !== "string" || entry.label.trim() === "") {
            throw new Error(`Hiányzó terméknév: ${entry.id}`);
        }
        ids.add(entry.id);
        return { id: entry.id, label: entry.label };
    });
    const [firstProduct] = products;
    if (!firstProduct) {
        throw new Error("A products.yaml nem tartalmaz érvényes terméklistát.");
    }
    const defaultProduct = typeof value.defaultProduct === "string" && ids.has(value.defaultProduct)
        ? value.defaultProduct
        : firstProduct.id;
    return { defaultProduct, products };
}
function validateVersions(value) {
    if (!isRecord(value) || !Array.isArray(value.versions) || value.versions.length === 0) {
        throw new Error("A versions.yaml nem tartalmaz érvényes verziólistát.");
    }
    const ids = new Set();
    const versions = value.versions.map((entry) => {
        if (!isRecord(entry) || typeof entry.id !== "string" || !versionIdPattern.test(entry.id)) {
            throw new Error("Érvénytelen verzióazonosító a versions.yaml fájlban.");
        }
        if (ids.has(entry.id)) {
            throw new Error(`Ismétlődő verzióazonosító: ${entry.id}`);
        }
        if (typeof entry.label !== "string" || entry.label.trim() === "") {
            throw new Error(`Hiányzó verziófelirat: ${entry.id}`);
        }
        if (typeof entry.releaseDate !== "string" || Number.isNaN(Date.parse(`${entry.releaseDate}T00:00:00Z`))) {
            throw new Error(`Érvénytelen kiadási dátum: ${entry.id}`);
        }
        ids.add(entry.id);
        return { id: entry.id, label: entry.label, releaseDate: entry.releaseDate };
    });
    const defaultVersion = typeof value.defaultVersion === "string" && ids.has(value.defaultVersion)
        ? value.defaultVersion
        : undefined;
    return { defaultVersion, versions };
}
function validateRevisions(itemPath, value) {
    if (!Array.isArray(value) || value.length === 0) {
        throw new Error(`Üres revíziólista: ${itemPath}`);
    }
    return value.map((entry) => {
        if (!isRecord(entry) || typeof entry.from !== "string" || !versionIdPattern.test(entry.from)) {
            throw new Error(`Érvénytelen revízió from értéke: ${itemPath}`);
        }
        if (entry.until !== undefined && (typeof entry.until !== "string" || !versionIdPattern.test(entry.until))) {
            throw new Error(`Érvénytelen revízió until értéke: ${itemPath}`);
        }
        if (typeof entry.file !== "string" || !revisionFilePattern.test(entry.file)) {
            throw new Error(`Érvénytelen revíziófájl: ${itemPath}`);
        }
        return { from: entry.from, until: entry.until, file: entry.file };
    });
}
function validateItems(value) {
    if (!isRecord(value) || !Array.isArray(value.items)) {
        throw new Error("A pages.yaml nem tartalmaz érvényes items listát.");
    }
    const pages = new Map();
    function visit(entries, path) {
        return entries.map((entry, index) => {
            const itemPath = `${path}.${index}`;
            if (!isRecord(entry) || typeof entry.title !== "string" || entry.title.trim() === "") {
                throw new Error(`Érvénytelen menüpont: ${itemPath}`);
            }
            const children = entry.children === undefined
                ? []
                : Array.isArray(entry.children)
                    ? visit(entry.children, itemPath)
                    : (() => { throw new Error(`Érvénytelen almenü: ${itemPath}`); })();
            let id;
            let revisions;
            let versionFile;
            let link;
            if (entry.link !== undefined) {
                if (typeof entry.link !== "string" || !(menuPageLinkPattern.test(entry.link) || menuExternalLinkPattern.test(entry.link))) {
                    throw new Error(`Érvénytelen link: ${itemPath}`);
                }
                if (entry.id !== undefined || entry.revisions !== undefined || entry.versionFile !== undefined || children.length > 0) {
                    throw new Error(`A hivatkozó menüpontnak nem lehet saját tartalma vagy almenüje: ${itemPath}`);
                }
                link = entry.link;
            }
            else if (entry.versionFile !== undefined) {
                if (entry.versionFile !== changelogTemplate) {
                    throw new Error(`Érvénytelen versionFile sablon: ${itemPath}`);
                }
                if (typeof entry.id !== "string" || !pageIdPattern.test(entry.id)) {
                    throw new Error(`Érvénytelen changelog azonosító: ${itemPath}`);
                }
                id = entry.id;
                versionFile = entry.versionFile;
            }
            else if (entry.id !== undefined) {
                if (typeof entry.id !== "string" || !pageIdPattern.test(entry.id)) {
                    throw new Error(`Érvénytelen oldalazonosító: ${String(entry.id)}`);
                }
                id = entry.id;
                revisions = validateRevisions(itemPath, entry.revisions);
            }
            if (id === undefined && link === undefined && children.length === 0) {
                throw new Error(`Üres menüpont: ${entry.title}`);
            }
            const tags = entry.tags === undefined
                ? []
                : Array.isArray(entry.tags) && entry.tags.every((tag) => typeof tag === "string")
                    ? entry.tags
                    : (() => { throw new Error(`Érvénytelen tags: ${entry.title}`); })();
            const keywords = entry.keywords === undefined
                ? []
                : Array.isArray(entry.keywords) && entry.keywords.every((keyword) => typeof keyword === "string")
                    ? entry.keywords
                    : (() => { throw new Error(`Érvénytelen kulcsszavak: ${entry.title}`); })();
            const description = entry.description === undefined
                ? undefined
                : typeof entry.description === "string" && entry.description.trim() !== ""
                    ? entry.description
                    : (() => { throw new Error(`Érvénytelen description: ${entry.title}`); })();
            const menu = entry.menu === undefined
                ? undefined
                : entry.menu === "header" || entry.menu === "sidebar"
                    ? entry.menu
                    : (() => { throw new Error(`Érvénytelen menu: ${entry.title}`); })();
            if (entry.expanded !== undefined && entry.expanded !== true) {
                throw new Error(`Érvénytelen expanded: ${entry.title}`);
            }
            const initiallyExpanded = entry.expanded === true;
            const item = { title: entry.title, id, revisions, versionFile, link, menu, initiallyExpanded, description, tags, keywords, children, key: itemPath };
            if (item.id) {
                if (pages.has(item.id)) {
                    throw new Error(`Ismétlődő oldalazonosító: ${item.id}`);
                }
                pages.set(item.id, item);
            }
            return item;
        });
    }
    // Egy "?page=<id>" link célja csak a teljes fa bejárása után ismert.
    function checkLinkTargets(list) {
        for (const item of list) {
            const target = linkedPageId(item);
            if (target !== undefined && !pages.has(target)) {
                throw new Error(`A link nem létező oldalra mutat: ${item.link}`);
            }
            checkLinkTargets(item.children);
        }
    }
    const items = visit(value.items, "item");
    checkLinkTargets(items);
    return { items, pages };
}
// Egy hivatkozó menüpont céloldala, ha a link a fa egy oldalára mutat
// ("?page=<id>"); külső címnél és nem hivatkozó menüpontnál undefined.
function linkedPageId(item) {
    return item.link !== undefined ? menuPageLinkPattern.exec(item.link)?.[1] : undefined;
}
// Resolves which physical Markdown file (if any) applies to an item at a given
// selected version — the rule itself lives in ./revisions.ts (shared with the
// build), this only supplies the loaded product's version order.
function resolveTargetFile(item, versionId) {
    return resolveFileAt(item, versionId, state.versionOrder);
}
function isDocItem(item) {
    return item.id !== undefined && (item.revisions !== undefined || item.versionFile !== undefined);
}
// Finds the id of the changelog item (the item backed by the per-version
// versionFile template) so a version can be opened straight to its changelog
// when no specific page was requested.
function findChangelogPageId(pages) {
    for (const item of pages.values()) {
        if (item.versionFile !== undefined && item.id !== undefined) {
            return item.id;
        }
    }
    return undefined;
}
// Egy hivatkozó menüpont ott látszik, ahol a céloldala; egy külső cím mindig.
function isVisibleAtVersion(item, versionId) {
    if (item.link !== undefined) {
        const target = linkedPageId(item);
        const targetItem = target !== undefined ? state.pages.get(target) : undefined;
        return target === undefined || (targetItem !== undefined && isVisibleAtVersion(targetItem, versionId));
    }
    if (isDocItem(item) && resolveTargetFile(item, versionId) !== undefined) {
        return true;
    }
    return item.children.some((child) => isVisibleAtVersion(child, versionId));
}
function hasVisiblePage(items, versionId) {
    return items.some((item) => isVisibleAtVersion(item, versionId));
}
async function loadContents(signal) {
    const product = state.product ?? "";
    const cached = cachedContents.get(product);
    if (cached) {
        return cached;
    }
    // "no-cache": a fájlnév minden kiadásban ugyanaz, ezért a böngésző ETag-gel
    // visszakérdez (304), és egy új kiadás után nem marad régi menüfa — a
    // keresési indexnek (search-index.json) ugyanabból a kiadásból kell jönnie.
    const yaml = await fetchText(contentUrl("pages.yaml"), signal, "no-cache");
    const result = validateItems(parse(yaml));
    cachedContents.set(product, result);
    return result;
}
async function fetchText(path, signal, cache) {
    const response = await fetch(path, { signal, cache });
    if (!response.ok) {
        throw new Error(`${path} (${response.status})`);
    }
    return response.text();
}
function stripFrontMatter(text) {
    return text.replace(frontMatterPattern, "");
}
function announce(message) {
    status.textContent = message;
}
// Betöltés közben a cikk megtartja az addigi magasságát: a kiürített cikk
// miatt a lap egy pillanatra összezsugorodna, a böngésző a nézetet a lap
// tetejére húzná, és a bal oldali sáv vele mozdulna. A következő tartalom
// beírása előtt a releaseArticleHeight() oldja fel.
function setArticleLoading() {
    article.style.minHeight = `${article.offsetHeight}px`;
    article.setAttribute("aria-busy", "true");
    article.innerHTML = "";
    const loading = document.createElement("div");
    loading.className = "loading-state";
    loading.innerHTML = '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span><span>Betöltés...</span>';
    article.append(loading);
    tocPanel.hidden = true;
    announce("A tartalom betöltése folyamatban.");
}
function releaseArticleHeight() {
    article.style.minHeight = "";
}
// Menüpontra (vagy a cikk egy ?page= linkjére) kattintva az új oldal nem a
// lap tetejéről (a hero fölül) indul, mert attól a bal oldali sáv is
// lecsúszna a helyéről. Ha a nézet már a cikk eleje alatt jár, a cikk
// elejéhez görget (a fix fejléc alá, a html scroll-padding-top-ja szerint)
// — a sticky sáv ilyenkor a helyén marad —, egyébként nem mozdul.
function revealDocumentStart() {
    const scrollPaddingTop = Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    if (article.getBoundingClientRect().top < scrollPaddingTop) {
        article.scrollIntoView({ block: "start", behavior: "auto" });
    }
}
function showArticleError(message) {
    releaseArticleHeight();
    article.setAttribute("aria-busy", "false");
    article.innerHTML = "";
    const error = document.createElement("div");
    error.className = "error-state";
    error.setAttribute("role", "alert");
    error.textContent = message;
    article.append(error);
    tocPanel.hidden = true;
    setHeroTitle(baseTitle());
    announce(message);
}
function showArticlePrompt(message) {
    releaseArticleHeight();
    article.setAttribute("aria-busy", "false");
    article.innerHTML = "";
    const prompt = document.createElement("p");
    prompt.className = "empty-state";
    prompt.textContent = message;
    article.append(prompt);
    tocPanel.hidden = true;
    document.title = baseTitle();
    setHeroTitle(baseTitle());
    announce(message);
}
// A címsor: ?product=<termék>&version=<verzió>&page=<oldal> (vagy &q=<keresés>),
// ebben a sorrendben; a cím többi paramétere (pl. kampánykód) a végén megmarad.
const routeParams = ["product", "version", "page", "q"];
function routePath(versionId, target, anchor) {
    const params = new URLSearchParams();
    if (state.product) {
        params.set("product", state.product);
    }
    params.set("version", versionId);
    if (target.query !== undefined) {
        params.set("q", target.query);
    }
    else if (target.page) {
        params.set("page", target.page);
    }
    for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (!routeParams.includes(key)) {
            params.append(key, value);
        }
    }
    return `${window.location.pathname}?${params}${anchor ? `#${encodeURIComponent(anchor)}` : ""}`;
}
function buildPageUrl(versionId, page, anchor) {
    return routePath(versionId, { page }, anchor);
}
// Egy "?page=<id>#<horgony>" link horgonya (dekódolva), vagy undefined.
function pageLinkAnchor(href) {
    const hashIndex = href.indexOf("#");
    const raw = hashIndex >= 0 ? href.slice(hashIndex + 1) : "";
    if (raw === "") {
        return undefined;
    }
    try {
        return decodeURIComponent(raw);
    }
    catch {
        return raw;
    }
}
// A részletes találati oldal címe. A "q" paraméternév szándékos: a GA4
// Enhanced Measurement "site search" alapból ezt figyeli.
function buildSearchUrl(versionId, query) {
    return routePath(versionId, { query });
}
function updateRoute(mode, options = {}) {
    // Minden navigáció (a Vissza gomb is) ide fut be: az asszisztens fejléce itt követi a
    // kiválasztott terméket és verziót.
    assistant?.refreshContext();
    if (mode === "none" || !state.selectedVersion) {
        return;
    }
    const route = routePath(state.selectedVersion, { page: state.selectedPage, query: state.searchQuery }, options.anchor);
    const historyState = options.highlight && options.highlight.length > 0 ? { searchHighlight: options.highlight } : null;
    if (mode === "push") {
        history.pushState(historyState, "", route);
    }
    else {
        history.replaceState(historyState, "", route);
    }
}
function renderVersions() {
    versionSelect.innerHTML = "";
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Válasszon verziót";
    placeholder.disabled = true;
    placeholder.selected = !state.selectedVersion;
    versionSelect.append(placeholder);
    // 2021.1 óta több mint 70 verzió van: évenként csoportosítva (a felirat
    // első négy jegye az év), a legújabb év elöl — state.versions már csökkenő.
    let group;
    for (const version of state.versions) {
        const year = /^\d{4}/.exec(version.label)?.[0] ?? "";
        if (!group || group.label !== year) {
            group = document.createElement("optgroup");
            group.label = year;
            versionSelect.append(group);
        }
        const option = document.createElement("option");
        option.value = version.id;
        option.textContent = version.label;
        option.selected = version.id === state.selectedVersion;
        group.append(option);
    }
    versionSelect.disabled = false;
}
function renderProducts() {
    productSelect.innerHTML = "";
    for (const product of state.products) {
        const option = document.createElement("option");
        option.value = product.id;
        option.textContent = product.label;
        option.selected = product.id === state.product;
        productSelect.append(option);
    }
    productSelect.disabled = false;
}
// Az induláskor kinyitandó (front matter "expanded: true") menüpontok, pl. a
// Modulok — a fa minden újraindulásakor (termék- és verzióváltás) újra.
function expandInitialItems(items) {
    for (const item of items) {
        if (item.initiallyExpanded) {
            state.expanded.add(item.key);
        }
        expandInitialItems(item.children);
    }
}
function expandActiveAncestors(items, page) {
    for (const item of items) {
        if (item.id === page) {
            return true;
        }
        if (expandActiveAncestors(item.children, page)) {
            state.expanded.add(item.key);
            return true;
        }
    }
    return false;
}
// Az aktuális oldal kiemelése a fában ÉS a sidebar találati listájában
// (undefined: egyik sem aktív, pl. a részletes találati oldalon).
function updateActivePageInTree(page) {
    searchResults.querySelectorAll("a[data-search-page]").forEach((link) => {
        const isActive = link.dataset.searchPage === page;
        link.classList.toggle("active", isActive);
        if (isActive) {
            link.setAttribute("aria-current", "page");
        }
        else {
            link.removeAttribute("aria-current");
        }
    });
    contentsTree.querySelectorAll("a.active").forEach((link) => {
        link.classList.remove("active");
        link.removeAttribute("aria-current");
    });
    if (!page) {
        return;
    }
    // A hivatkozó menüpont (data-alias) nem kap kiemelést: az oldal a saját
    // helyén látszik aktívnak a fában.
    const activeLink = contentsTree.querySelector(`a[data-page="${CSS.escape(page)}"]:not([data-alias])`);
    if (!activeLink) {
        renderTree();
        return;
    }
    activeLink.classList.add("active");
    activeLink.setAttribute("aria-current", "page");
}
function renderTree() {
    contentsTree.innerHTML = "";
    const versionId = state.selectedVersion;
    let visiblePages = 0;
    function renderList(items) {
        const list = document.createElement("ul");
        list.className = "tree-list";
        for (const item of items) {
            if (item.menu === "header" || (versionId && !isVisibleAtVersion(item, versionId))) {
                continue;
            }
            const listItem = document.createElement("li");
            const row = document.createElement("div");
            row.className = "tree-row";
            const isExpanded = state.expanded.has(item.key);
            if (item.children.length > 0) {
                const toggle = document.createElement("button");
                toggle.type = "button";
                toggle.className = "tree-toggle";
                toggle.dataset.treeKey = item.key;
                toggle.setAttribute("aria-expanded", String(isExpanded));
                toggle.setAttribute("aria-label", `${item.title} ${isExpanded ? "összecsukása" : "kinyitása"}`);
                row.append(toggle);
            }
            else {
                const spacer = document.createElement("span");
                spacer.className = "tree-spacer";
                spacer.setAttribute("aria-hidden", "true");
                row.append(spacer);
            }
            const link = createMenuLink(item, versionId, "tree-link");
            if (link) {
                if (isDocItem(item)) {
                    visiblePages += 1;
                    if (item.id === state.selectedPage) {
                        link.classList.add("active");
                        link.setAttribute("aria-current", "page");
                    }
                }
                row.append(link);
            }
            else {
                const label = document.createElement("span");
                label.className = "tree-label";
                label.textContent = item.title;
                row.append(label);
            }
            listItem.append(row);
            if (item.children.length > 0 && isExpanded) {
                listItem.append(renderList(item.children));
            }
            list.append(listItem);
        }
        return list;
    }
    contentsTree.append(renderList(state.items));
    treeEmpty.hidden = visiblePages > 0;
    treeEmpty.textContent = "Ehhez a verzióhoz nem található dokumentáció.";
    applySidebarMode();
}
// Egy menüpont linkje a fában és a fejlécben: a saját oldala vagy egy
// hivatkozó menüpont céloldala a kiválasztott verzióban (data-page — a
// kattintást a fa és a fejléc kezeli, helyben tölti be), illetve külső cím, új
// lapon, mint a cikkek külső linkjei. Saját oldal nélküli kategóriánál
// undefined.
function createMenuLink(item, versionId, className) {
    const page = isDocItem(item) ? item.id : linkedPageId(item);
    const link = document.createElement("a");
    if (className) {
        link.className = className;
    }
    link.textContent = item.title;
    if (page !== undefined) {
        link.href = versionId ? buildPageUrl(versionId, page) : "#";
        link.dataset.page = page;
        if (item.link !== undefined) {
            link.dataset.alias = "";
        }
    }
    else if (item.link !== undefined) {
        link.href = item.link;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
    }
    else {
        return undefined;
    }
    return link;
}
// A fejléc fő-navigációja a tartalomjegyzék legfelső szintje: ugyanazok a
// menüpontok (a csak a bal oldali menübe szántak — menu: sidebar — nélkül),
// ugyanabban a sorrendben, és ugyanúgy csak a kiválasztott verzióban
// láthatók — lásd ../../data-store.md "Hivatkozó menüpontok és a
// fejléc-menü". Saját oldal nélküli kategória nem kerül ide, mert nincs hova
// vezetnie.
function renderSiteNav() {
    siteNavList.innerHTML = "";
    const versionId = state.selectedVersion;
    for (const item of state.items) {
        if (item.menu === "sidebar" || (versionId && !isVisibleAtVersion(item, versionId))) {
            continue;
        }
        const link = createMenuLink(item, versionId);
        if (link) {
            const listItem = document.createElement("li");
            listItem.append(link);
            siteNavList.append(listItem);
        }
    }
}
// Every local reference in content Markdown (an image or attachment, since ?page=
// links are logical and everything else is rejected by validate-data.mjs) is a
// plain file name next to the Markdown file itself — so it resolves against that
// file's own directory, see ../../data-store.md. "file" is the path within the
// content/ folder, the product folder included ("conto.net/pages/…").
function documentBase(file) {
    const directory = file.split("/").slice(0, -1).map(encodeURIComponent).join("/");
    return new URL(`../content/${directory}/`, window.location.href);
}
// A Markdownba illesztett CobraConto.Net menüpont-link ("[Felirat](ccnl://…)",
// lásd ccnl.ts) a helyén gomb lesz, ami a programban megnyitja az ablakot. A
// href-et a GUID-ból újraépítjük (csak típus + GUID, szerver/katalógus soha), a
// hibás linkből sima szöveg marad — a tartalomban a validate-data.mjs jelzi.
function prepareProgramLink(link, href) {
    const parsed = parseCcnlLink(href);
    if (!parsed.ok) {
        link.replaceWith(...link.childNodes);
        return;
    }
    link.href = parsed.link.href;
    link.className = "cs-btn cs-btn-secondary program-link";
    link.title = "Megnyitás a CobraConto.Net programban — telepített program kell hozzá, a böngésző rákérdez az indításra.";
}
function prepareDocumentContent(root, base) {
    const stats = { images: 0, relativeImages: 0, links: 0, programLinks: 0, tables: 0 };
    if (!state.selectedVersion) {
        return stats;
    }
    for (const image of root.querySelectorAll("img[src]")) {
        stats.images += 1;
        const source = image.getAttribute("src") ?? "";
        const url = new URL(source, base).href;
        if (!/^(?:[a-z]+:|\/|#)/i.test(source)) {
            stats.relativeImages += 1;
            image.src = url;
        }
        // Eredeti méret: a kép egy pixele a kijelző egy pixele. A böngésző a képet
        // alapból CSS-pixelben rajzolja, ami pl. 150%-os Windows-skálázásnál
        // másfélszeresére nagyítja (és elmossa) — a képernyőképek pedig maguk is
        // a készítő gépének skálázásával készülnek. A devicePixelRatio-s
        // sűrűség-jelölővel a kép CSS-szélessége natív szélesség / devicePixelRatio
        // lesz (a .document-content img max-width-je továbbra is korlátozza).
        image.srcset = `${url} ${window.devicePixelRatio}x`;
        image.loading = "lazy";
        image.decoding = "async";
        image.fetchPriority = "low";
    }
    for (const link of root.querySelectorAll("a[href]")) {
        stats.links += 1;
        const href = link.getAttribute("href") ?? "";
        if (isCcnlHref(href)) {
            stats.programLinks += 1;
            prepareProgramLink(link, href);
            continue;
        }
        const logicalMatch = logicalPageLinkPattern.exec(href);
        const targetId = logicalMatch?.[1];
        if (targetId && state.pages.has(targetId)) {
            // "?page=<id>#<horgony>": a céloldal egy szakaszára mutat (a horgony a
            // heading-slug.ts szerinti címsor-id), mint a keresési találatok.
            const anchor = pageLinkAnchor(href);
            link.dataset.docPage = targetId;
            if (anchor) {
                link.dataset.docAnchor = anchor;
            }
            link.href = buildPageUrl(state.selectedVersion, targetId, anchor);
            continue;
        }
        if (href.startsWith("#")) {
            continue;
        }
        if (!/^(?:[a-z]+:|\/|\?)/i.test(href)) {
            link.href = new URL(href, base).href;
        }
        if (link.origin !== window.location.origin) {
            link.target = "_blank";
            link.rel = "noopener noreferrer";
        }
    }
    // A csomagoló osztálya a www.cobra.hu-val közös Markdown-táblázat
    // komponensé (design-system.css ".doc-table-wrap").
    for (const table of root.querySelectorAll("table")) {
        stats.tables += 1;
        if (table.parentElement?.classList.contains("doc-table-wrap")) {
            continue;
        }
        const wrapper = document.createElement("div");
        wrapper.className = "doc-table-wrap";
        table.before(wrapper);
        wrapper.append(table);
    }
    return stats;
}
// A tartalomjegyzék-építés (horgony-id-k kiosztása, beágyazott lista,
// scroll-spy) a www.cobra.hu-val KÖZÖS Brand/Web/ts/markdown.ts
// buildHeadingToc()-jában él — lásd ott a részletes hátteret. A lista
// felépítése (osztálynevek, h2/h3 beágyazás) a megosztott design-system.css
// ".cs-docs-toc" kártyájáét követi (a színezése az ".aszf-toc-list"-ével
// egyezik, lásd ott).
//
// FONTOS SORREND: ezt buildDocumentAccordion() ELŐTT kell hívni — a horgony-
// id-ket a még lapos (nem szakaszokba tördelt) H2/H3-akra osztja ki, a
// buildDocumentAccordion() pedig utána, ugyanazokat az elem-referenciákat
// mozgatja a kinyitható kártyákba (az id-k és a scroll-spy IntersectionObserver
// megfigyelése az elemek DOM-beli áthelyezése után is érvényben marad).
function buildPageToc() {
    tocController?.disconnect();
    tocController = buildHeadingToc(article, pageToc, {
        levels: ["h2", "h3"],
        listClassName: "cs-docs-toc__list",
        sublistClassName: "cs-docs-toc__sublist",
        scrollSpyRootMargin: "-15% 0px -70% 0px",
        onEmpty: () => {
            tocPanel.hidden = true;
        },
    });
    if (pageToc.childElementCount > 0) {
        tocPanel.hidden = false;
    }
}
// A cikk H2-vel jelölt szakaszait (cím + a következő H2-ig terjedő tartalom)
// kinyitható/összecsukható kártyákká alakítja, UGYANAZZAL a megosztott
// design-system.css ".cs-faq-item"/".cs-faq-question__icon" komponenssel,
// mint amit a www.cobra.hu GYIK oldala használ a kérdés/válasz párokra
// (lásd Web/Www/src/ts/gyik.ts renderQuestion()/setupToggle()) —
// lásd a részletes indoklást styles.css ".document-content .cs-faq-item > h2"
// kommentjében. A H2 elem maga (és a rajta lévő horgony-id) nem tűnik el,
// csak egy gomb (".doc-accordion__toggle") kerül BELÉ — így a tartalomjegyzék/
// scroll-spy (buildPageToc(), lásd fent) és a közvetlen horgonylinkek
// (pl. külső oldalról #cím-szoveg-re mutató linkek) továbbra is működnek.
//
// A H1 előtti/H1 utáni, még H2-vel nem tagolt bevezető tartalom ("intro")
// érintetlen, mindig látható marad — csak a H2-vel kezdődő szakaszok
// alakulnak kártyává. Ha a cikkben nincs egyetlen H2 sem (rövid, egyszakaszos
// oldal), a függvény nem csinál semmit — a tartalom a korábbi, teljesen
// nyitott formájában marad.
function buildDocumentAccordion(container) {
    const children = Array.from(container.children);
    const firstSectionIndex = children.findIndex((child) => child.tagName === "H2");
    if (firstSectionIndex === -1) {
        return;
    }
    const sections = [];
    for (const child of children.slice(firstSectionIndex)) {
        if (child.tagName === "H2") {
            sections.push({ heading: child, body: [] });
            continue;
        }
        const current = sections[sections.length - 1];
        current?.body.push(child);
    }
    const list = document.createElement("div");
    list.className = "cs-faq-list doc-accordion";
    sections.forEach((section, index) => {
        const item = document.createElement("div");
        // Alapból nyitva (lásd lejjebb az article "click" eseményfigyelőjének
        // ".doc-accordion__toggle" ágát, ami ugyanezt az "is-open"/
        // aria-expanded/hidden hármast kapcsolja ki/be kattintásra) — a
        // kártyák tartalma így betöltéskor rögtön látható, a felhasználó csak
        // akkor csukja be, amelyik nem érdekli.
        item.className = "cs-faq-item is-open";
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "doc-accordion__toggle";
        toggle.id = `doc-accordion-toggle-${index}`;
        toggle.setAttribute("aria-expanded", "true");
        const body = document.createElement("div");
        body.className = "cs-faq-answer";
        body.id = `doc-accordion-panel-${index}`;
        body.hidden = false;
        body.setAttribute("role", "region");
        body.setAttribute("aria-labelledby", toggle.id);
        toggle.setAttribute("aria-controls", body.id);
        // A H2 eredeti tartalmát (szöveg, esetleges beágyazott elemek) a gombba
        // költöztetjük — a H2 maga üresen, csak a gombot tartalmazva marad meg,
        // ezért a betűstílust a gomb "font: inherit"-tel a H2-től veszi át
        // (lásd styles.css ".doc-accordion__toggle"). EGYETLEN címke-elembe
        // csomagolva: a gomb flex-tároló ("space-between"), és ha a cím több
        // csomópontból áll (félkövér/kód/link a címben, vagy a keresési
        // kiemelés <mark>-ja, lásd highlightSearchTerms()), csomagolás nélkül
        // minden darab külön flex-elem lenne, és a sor szétesne.
        const label = document.createElement("span");
        label.className = "doc-accordion__label";
        while (section.heading.firstChild) {
            label.append(section.heading.firstChild);
        }
        const icon = document.createElement("span");
        icon.className = "cs-faq-question__icon";
        icon.setAttribute("aria-hidden", "true");
        toggle.append(label, icon);
        section.heading.append(toggle);
        body.append(...section.body);
        item.append(section.heading, body);
        list.append(item);
    });
    container.append(list);
}
// Ha egy horgony-id (a jobb oldali TOC-linkből, egy cikken belüli linkből
// vagy az URL kezdeti #hash-éből) egy összecsukott ".cs-faq-item" belsejébe
// mutat, ez nyitja ki, mielőtt a hívó a heading elemre görgetne — enélkül a
// böngésző/scrollIntoView egy "hidden" (nulla magasságú) elemre próbálna
// görgetni.
function expandAccordionItem(item) {
    const body = item.querySelector(".cs-faq-answer");
    const toggle = item.querySelector(".doc-accordion__toggle");
    if (!body || !toggle || !body.hidden) {
        return;
    }
    body.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    item.classList.add("is-open");
}
function scrollToHeading(id) {
    const heading = document.getElementById(id);
    if (!heading) {
        return false;
    }
    const item = heading.closest(".cs-faq-item");
    if (item) {
        expandAccordionItem(item);
    }
    heading.scrollIntoView();
    keepHeadingInView(heading);
    return true;
}
// A célszakasz fölötti képek lusta betöltésűek és méret nélküliek: a görgetés
// UTÁN töltődnek be, és lejjebb tolják a címsort. Amíg be nem töltődnek
// (legfeljebb néhány másodpercig), és a felhasználó maga nem görget, minden
// betöltés után újra a címsorhoz igazítunk.
function keepHeadingInView(heading) {
    const pending = [...article.querySelectorAll("img")].filter((image) => !image.complete && image.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING);
    if (pending.length === 0) {
        return;
    }
    const watch = new AbortController();
    const stop = () => watch.abort();
    for (const type of ["wheel", "touchstart", "mousedown", "keydown"]) {
        window.addEventListener(type, stop, { once: true, passive: true, signal: watch.signal });
    }
    window.setTimeout(stop, 5000);
    let remaining = pending.length;
    const realign = () => {
        if (watch.signal.aborted) {
            return;
        }
        if (heading.isConnected) {
            heading.scrollIntoView();
        }
        remaining -= 1;
        if (remaining === 0 || !heading.isConnected) {
            stop();
        }
    };
    for (const image of pending) {
        image.addEventListener("load", realign, { once: true, signal: watch.signal });
        image.addEventListener("error", realign, { once: true, signal: watch.signal });
    }
}
// Egy cikken belüli (#horgony) linkre kattintva a natív böngésző-ugrás
// helyett előbb kinyitjuk a célszakaszt (lásd scrollToHeading()), utána
// görgetünk — ugyanezt a #page-toc panelben és magában a cikkben (pl. egy
// Markdown-tartalom belső kereszthivatkozásánál) is kezelnünk kell.
function handleInPageAnchorClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) {
        return;
    }
    const rawId = link.getAttribute("href")?.slice(1) ?? "";
    if (rawId === "") {
        return;
    }
    const id = decodeURIComponent(rawId);
    const heading = document.getElementById(id);
    if (!heading || !article.contains(heading)) {
        return;
    }
    event.preventDefault();
    history.pushState(null, "", `#${rawId}`);
    scrollToHeading(id);
}
function renderMarkdown(markdown, cacheKey, focusDocument, profiler) {
    profiler.step("Rendering started.", { markdownCharacters: markdown.length });
    let cleanHtml = renderedPageCache.get(cacheKey);
    if (cleanHtml === undefined) {
        profiler.step("Markdown to HTML conversion started.");
        const rendered = parseMarkdown(markdown);
        profiler.step("Markdown to HTML conversion finished.", { htmlCharacters: rendered.length });
        profiler.step("HTML sanitization started.");
        // A "ccnl://" menüpont-linkeket a DOMPurify ismeretlen sémaként eldobná —
        // a prepareDocumentContent() alakítja őket gombbá.
        cleanHtml = sanitizeHtml(rendered, { keepHref: isCcnlHref });
        profiler.step("HTML sanitization finished.", { sanitizedCharacters: cleanHtml.length });
        renderedPageCache.set(cacheKey, cleanHtml);
        profiler.step("Sanitized HTML stored in the render cache.");
    }
    else {
        profiler.step("Sanitized HTML render cache hit.", { sanitizedCharacters: cleanHtml.length });
    }
    profiler.step("Detached DOM parsing started.");
    const template = document.createElement("template");
    template.innerHTML = cleanHtml;
    profiler.step("Detached DOM parsing finished.");
    // A cacheKey maga a betöltött Markdown-fájl útja (lásd loadPage) — a képek
    // ennek a mappájához képest oldódnak fel.
    const contentStats = prepareDocumentContent(template.content, documentBase(cacheKey));
    profiler.step("Relative asset and document URLs rewritten.", contentStats);
    profiler.step("Images configured for lazy loading and asynchronous decoding.", {
        images: contentStats.images,
        relativeImages: contentStats.relativeImages,
    });
    releaseArticleHeight();
    article.replaceChildren(template.content);
    // A ragadó táblázatfejléchez (design-system.css ".doc-table-wrap.is-fit");
    // a beillesztés után, mert a mérés a dokumentumban álló csomagolón megy.
    observeTableWraps(article);
    profiler.step("Prepared document inserted into the page.");
    buildPageToc();
    profiler.step("Right-side heading navigation generated.", {
        headings: pageToc.querySelectorAll("a").length,
    });
    buildDocumentAccordion(article);
    profiler.step("Collapsible sections built.", {
        sections: article.querySelectorAll(".cs-faq-item").length,
    });
    // Keresési találatról nyitva (lásd PageOpenOptions): a horgonyok és a
    // harmonika UTÁN, hogy a <mark>-ok a végleges DOM-ba kerüljenek, és a
    // címsor-id-ket (a még jelöletlen szövegből) már kiosztották.
    const highlightTerms = readHighlightState();
    const firstHit = highlightTerms ? highlightSearchTerms(article, highlightTerms) : undefined;
    if (highlightTerms) {
        profiler.step("Search terms highlighted.", { marks: article.querySelectorAll("mark.search-hit").length });
    }
    article.setAttribute("aria-busy", "false");
    const title = article.querySelector("h1")?.textContent?.trim();
    document.title = title ? `${title} | ${baseTitle()}` : baseTitle();
    setHeroTitle(title ?? baseTitle());
    announce(`${title ?? "A dokumentum"} betöltve.`);
    if (window.location.hash) {
        const targetId = decodeURIComponent(window.location.hash.slice(1));
        // Ha a horgony mégsem létezik (pl. a tartalom az index építése óta
        // változott, vagy egy ?page=<id>#<horgony> link célszakasza ebben a
        // verzióban nincs meg), legalább az első kiemelt találatig görgetünk,
        // annak híján a cikk elejére, mint egy horgony nélküli linknél.
        requestAnimationFrame(() => {
            if (!scrollToHeading(targetId)) {
                if (firstHit) {
                    firstHit.scrollIntoView({ block: "center" });
                }
                else if (focusDocument) {
                    revealDocumentStart();
                }
            }
        });
    }
    else if (firstHit) {
        requestAnimationFrame(() => firstHit.scrollIntoView({ block: "center" }));
        if (focusDocument) {
            article.focus({ preventScroll: true });
        }
    }
    else if (focusDocument) {
        revealDocumentStart();
        article.focus({ preventScroll: true });
    }
    profiler.step("Page load complete.");
}
// A változásnapló PDF-jét a build készíti a .md mellé (content/<termék>/
// changelogs/<verzió>.pdf, lásd src/build-changelog-pdfs.mjs). A dev szerveren
// nincs PDF, ezért a gomb csak akkor kerül a cikk elejére, ha a fájl létezik. A
// letöltött fájl neve a korábbi WordPress-es PDF-ek mintáját követi
// ("CobraContoNet-2026.3SP1-valtozasnaplo.pdf"), a termék nevével.
async function addChangelogPdfLink(versionId, pageId, signal) {
    const product = state.product;
    const href = contentUrl(`changelogs/${encodeURIComponent(versionId)}.pdf`);
    const response = await fetch(href, { method: "HEAD", signal }).catch(() => undefined);
    if (!response?.ok || signal.aborted || state.product !== product || state.selectedPage !== pageId || state.selectedVersion !== versionId) {
        return;
    }
    const label = state.versions.find((version) => version.id === versionId)?.label ?? versionId;
    const fileProduct = productLabel().normalize("NFD").replace(/[^A-Za-z0-9]/g, "");
    const paragraph = document.createElement("p");
    paragraph.className = "changelog-pdf";
    const link = document.createElement("a");
    link.className = "cs-btn cs-btn-secondary";
    link.href = href;
    link.download = `${fileProduct}-${label.replace(/\s+/g, "")}-valtozasnaplo.pdf`;
    link.textContent = "PDF letöltése";
    paragraph.append(link);
    article.prepend(paragraph);
}
// A cikk első sora: melyik programverziótól érvényes a megnyitott
// revíziófájl (a pages.yaml-beli "from"). A változásnaplóra (versionFile) nem
// kerül ki — az maga a kiválasztott verzió naplója.
function addRevisionNote(item, file) {
    const from = item.revisions?.find((revision) => revision.file === file)?.from;
    if (from === undefined) {
        return;
    }
    const version = document.createElement("strong");
    version.textContent = state.versions.find((entry) => entry.id === from)?.label ?? from;
    const note = document.createElement("p");
    note.className = "page-version";
    note.append("Érvényes: ", version, " verziótól");
    article.prepend(note);
}
async function loadPage(pageId, mode, controller, options = {}) {
    state.searchQuery = undefined;
    const item = state.pages.get(pageId);
    const resolvedFile = state.selectedVersion && item ? resolveTargetFile(item, state.selectedVersion) : undefined;
    if (!state.selectedVersion || !item || !resolvedFile || !pageIdPattern.test(pageId)) {
        showArticleError(item ? "A dokumentum a kiválasztott programverzióban nem érhető el." : "A kért dokumentum nem található.");
        return;
    }
    const profiler = createLoadProfiler(state.selectedVersion, pageId);
    profiler.step("Page selection received.");
    if (!controller) {
        if (requestController) {
            profiler.step("Previous request cancellation requested.");
        }
        requestController?.abort();
        requestController = new AbortController();
        controller = requestController;
    }
    state.selectedPage = pageId;
    expandActiveAncestors(state.items, pageId);
    updateActivePageInTree(pageId);
    profiler.step("Selected tree item highlighted.");
    updateRoute(mode, options);
    profiler.step("Browser URL and history updated.", { historyMode: mode });
    setArticleLoading();
    profiler.step("Loading indicator displayed.");
    // A tartalommappán belüli út a termékkel együtt — a gyorsítótárak kulcsa, és
    // ehhez képest oldódnak fel a képek (documentBase()).
    const cacheKey = `${state.product}/${resolvedFile}`;
    try {
        let markdown = pageCache.get(cacheKey);
        if (markdown === undefined) {
            const path = `../content/${cacheKey.split("/").map(encodeURIComponent).join("/")}`;
            profiler.step("Markdown download started.", { path });
            // "no-cache", mint a pages.yaml-nál: egy revíziófájl helyben szerkesztve is
            // ugyanazon a néven marad, és a szerver nem küld Cache-Control-t, így a
            // böngésző heurisztikusan órákig a régi szöveget adná az új menüfa mellé.
            markdown = stripFrontMatter(await fetchText(path, controller.signal, "no-cache"));
            pageCache.set(cacheKey, markdown);
            profiler.step("Markdown download finished and response cached.", { characters: markdown.length });
        }
        else {
            profiler.step("Markdown response cache hit.", { characters: markdown.length });
        }
        if (!controller.signal.aborted && state.selectedPage === pageId) {
            profiler.step("Waiting one animation frame before rendering.");
            await new Promise((resolve) => requestAnimationFrame(() => resolve()));
            profiler.step("Animation frame reached; synchronous rendering can begin.");
            if (!controller.signal.aborted && state.selectedPage === pageId) {
                renderMarkdown(markdown, cacheKey, mode === "push", profiler);
                if (item.versionFile) {
                    void addChangelogPdfLink(state.selectedVersion, pageId, controller.signal);
                }
                else {
                    addRevisionNote(item, resolvedFile);
                }
            }
        }
    }
    catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            profiler.step("Page load cancelled.");
            return;
        }
        profiler.step("Page load failed.", { error: error instanceof Error ? error.message : String(error) });
        console.error("A dokumentum betöltése sikertelen:", error);
        showArticleError("A tartalom nem tölthető be.");
    }
    finally {
        if (requestController === controller) {
            requestController = undefined;
        }
    }
}
async function loadVersion(versionId, target, mode) {
    const version = state.versions.find((entry) => entry.id === versionId);
    if (!version || !versionIdPattern.test(versionId)) {
        showArticleError("A kért programverzió nem található.");
        return;
    }
    requestController?.abort();
    const controller = new AbortController();
    requestController = controller;
    state.selectedVersion = versionId;
    state.selectedPage = undefined;
    state.searchQuery = undefined;
    state.expanded.clear();
    searchInput.disabled = true;
    // A keresés hatóköre a kiválasztott verzió — a mező ezt ki is mondja (a
    // verziófeliratok mind "20…"-szal kezdődnek, ezért "a", nem "az").
    searchInput.placeholder = `Keresés a ${version.label} verzióban…`;
    versionSelect.value = versionId;
    contentsTree.innerHTML = "";
    treeEmpty.hidden = true;
    setArticleLoading();
    try {
        const contents = await loadContents(controller.signal);
        if (controller.signal.aborted || state.selectedVersion !== versionId) {
            return;
        }
        state.items = contents.items;
        state.pages = contents.pages;
        expandInitialItems(state.items);
        searchInput.disabled = false;
        localStorage.setItem(`${storageKey}:${state.product}`, versionId);
        renderTree();
        renderSiteNav();
        // Verzióváltáskor a sidebar találati listája az új verzióra fut újra.
        if (isSidebarSearchActive()) {
            scheduleSidebarSearch(0);
        }
        if (target.query !== undefined) {
            await showSearchPage(target.query, mode, controller);
            return;
        }
        // No explicit page was requested (e.g. the user just picked a version, or
        // opened the help site with no page in the URL): default to the changelog
        // for the selected version instead of an empty "choose a document" prompt.
        const effectivePage = target.page ?? findChangelogPageId(state.pages);
        const requestedItem = effectivePage ? state.pages.get(effectivePage) : undefined;
        const requestedResolved = requestedItem ? resolveTargetFile(requestedItem, versionId) : undefined;
        if (!effectivePage || !requestedResolved) {
            updateRoute(mode);
            showArticlePrompt(hasVisiblePage(state.items, versionId)
                ? "Válasszon dokumentumot a tartalomjegyzékből."
                : "Ehhez a verzióhoz nem található dokumentáció.");
            return;
        }
        await loadPage(effectivePage, mode, controller, { anchor: target.page ? target.anchor : undefined });
    }
    catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            return;
        }
        console.error("A tartalomjegyzék betöltése sikertelen:", error);
        showArticleError("A tartalomjegyzék nem tölthető be.");
    }
}
// Termékváltás (a bal oldali lenyíló, a címsor ?product= paramétere): a termék
// verziólistája, majd a kért (vagy az alapértelmezett) verzió — ugyanúgy, mint
// induláskor. Minden termékfüggő állapot (menüfa, keresés, kinyitott ágak)
// újraindul; a gyorsítótárak termékenként maradnak.
async function loadProduct(productId, target, mode) {
    const product = state.products.find((entry) => entry.id === productId);
    if (!product) {
        showArticleError("A kért termék nem található.");
        return;
    }
    requestController?.abort();
    requestController = undefined;
    state.product = product.id;
    state.versions = [];
    state.versionOrder = new Map();
    state.defaultVersion = undefined;
    state.selectedVersion = undefined;
    state.selectedPage = undefined;
    state.searchQuery = undefined;
    state.items = [];
    state.pages = new Map();
    state.expanded.clear();
    productSelect.value = product.id;
    heroLead.textContent = `${product.label} verziókövető tudásbázis és dokumentáció.`;
    versionSelect.disabled = true;
    searchInput.disabled = true;
    contentsTree.innerHTML = "";
    treeEmpty.hidden = true;
    renderSiteNav();
    setArticleLoading();
    document.title = baseTitle();
    setHeroTitle(baseTitle());
    try {
        const versionsText = await fetchText(contentUrl("versions.yaml"), undefined, "no-cache");
        if (state.product !== product.id) {
            return;
        }
        const versionsDocument = validateVersions(parse(versionsText));
        const collator = new Intl.Collator("hu", { numeric: true, sensitivity: "base" });
        state.versions = [...versionsDocument.versions].sort((left, right) => right.releaseDate.localeCompare(left.releaseDate) || collator.compare(right.label, left.label));
        state.versionOrder = buildVersionOrder(versionsDocument.versions);
        state.defaultVersion = versionsDocument.defaultVersion;
        renderVersions();
        // If the URL doesn't pin a specific version (e.g. a bare help site load, or
        // the user just picked a product), default to the product's most recent
        // program version instead of leaving the picker empty.
        const initialVersion = target.version && state.versions.some((version) => version.id === target.version)
            ? target.version
            : state.defaultVersion ?? state.versions[0]?.id;
        if (initialVersion) {
            await loadVersion(initialVersion, target, mode);
        }
    }
    catch (error) {
        if (state.product !== product.id) {
            return;
        }
        console.error("A termék verziólistája nem tölthető be:", error);
        versionSelect.innerHTML = "<option>Nem érhető el</option>";
        showArticleError("A dokumentáció verziólistája nem tölthető be.");
    }
}
function readRoute() {
    const params = new URLSearchParams(window.location.search);
    const product = params.get("product") ?? undefined;
    const version = params.get("version") ?? undefined;
    const page = params.get("page") ?? undefined;
    const query = params.get("q")?.trim().slice(0, maxQueryLength);
    let anchor;
    try {
        anchor = decodeURIComponent(window.location.hash.slice(1)) || undefined;
    }
    catch {
        anchor = undefined;
    }
    return {
        product: product && productIdPattern.test(product) ? product : undefined,
        version: version && versionIdPattern.test(version) ? version : undefined,
        page: page && pageIdPattern.test(page) ? page : undefined,
        query: query ? query : undefined,
        anchor,
    };
}
// ---------------------------------------------------------------------------
// Teljes szöveges keresés. A mag (index, lekérdezés, csoportosítás, snippet)
// a ./search.ts-ben, az index a build terméke (../search-index.mjs) — itt
// csak a bekötés: lusta index-betöltés, a verzió szerinti láthatóság, a
// sidebar találati listája (a fa helyén), a részletes találati oldal (?q=)
// és a kiemelés a találatról megnyitott oldalon. Lásd README "Keresés".
// ---------------------------------------------------------------------------
// A kiválasztott termék mappájában (content/<termék>/search-index.json), lásd
// webpack.config.js createSearchIndexPlugin.
const searchIndexFile = "search-index.json";
const maxQueryLength = 200;
const searchDebounceMs = 120;
const sidebarResultLimit = 25;
const sidebarSnippetLength = 110;
const pageSnippetLength = 220;
const sectionsPerPageHit = 3;
// A sidebarban gépelt keresést csak ennyi szünet után mérjük (ha a látogató
// hozzájárult), hogy ne minden leütés legyen egy esemény.
const searchTrackingDelayMs = 1500;
const crumbSeparator = " › ";
// Termékenként egy keresőmotor (a termék saját indexéből).
const searchEngines = new Map();
const searchEnginePromises = new Map();
let sidebarSearchTimer;
let searchTrackingTimer;
let lastTrackedSearch = "";
// Kulcs: "<termék> <verzió>" — két termékben lehet azonos verzióazonosító.
const visibleFilesByVersion = new Map();
function isDrawerLayout() {
    return window.matchMedia("(max-width: 991.98px)").matches;
}
// Az index fájl-útjait (revisions[].file, changelogs/<verzió>.md) az oldal
// keresésre szánt metaadatára képezi — a pages.yaml-ből, így a cím/kulcsszó/
// leírás egy helyen él, nem kerül bele az indexbe.
function buildSearchPages(items, versions) {
    const byFile = new Map();
    function visit(list, crumbs) {
        for (const item of list) {
            if (item.id && isDocItem(item)) {
                const info = {
                    id: item.id,
                    title: item.title,
                    keywords: [...item.keywords, ...item.tags],
                    description: item.description,
                    crumbs,
                };
                for (const revision of item.revisions ?? []) {
                    byFile.set(revision.file, info);
                }
                if (item.versionFile) {
                    for (const version of versions) {
                        byFile.set(item.versionFile.replace("{version}", version.id), info);
                    }
                }
            }
            visit(item.children, [...crumbs, item.title]);
        }
    }
    visit(items, []);
    return byFile;
}
// Lusta: az első kereséskor (vagy a mező első fókuszakor) tölt le, egyszer —
// termékenként a kiválasztott termék indexét. Hiba után a következő próbálkozás
// újra letölti.
function ensureSearchEngine() {
    const product = state.product ?? "";
    let promise = searchEnginePromises.get(product);
    if (!promise) {
        const versions = state.versions;
        const pending = (async () => {
            const [indexText, contents] = await Promise.all([
                fetchText(contentUrl(searchIndexFile), undefined, "no-cache"),
                loadContents(),
            ]);
            const pages = buildSearchPages(contents.items, versions);
            const engine = await createSearchEngine(parseSearchIndex(JSON.parse(indexText)), (file) => pages.get(file));
            searchEngines.set(product, engine);
            return engine;
        })();
        promise = pending;
        searchEnginePromises.set(product, pending);
        pending.catch((error) => {
            console.error("A keresési index betöltése sikertelen:", error);
            if (searchEnginePromises.get(product) === pending) {
                searchEnginePromises.delete(product);
            }
        });
    }
    return promise;
}
function prefetchSearchIndex() {
    if (state.selectedVersion) {
        ensureSearchEngine().catch(() => undefined);
    }
}
// Egy verzióban látható fájlok — ugyanaz a resolveTargetFile()-szabály, ami a
// fát és az oldalt is meghatározza (a változásnaplónál a versionFile-sablon).
function visibleFilesAt(versionId) {
    const key = `${state.product} ${versionId}`;
    const cached = visibleFilesByVersion.get(key);
    if (cached) {
        return cached;
    }
    const files = visibleFiles(state.pages.values(), versionId, state.versionOrder);
    if (state.pages.size > 0) {
        visibleFilesByVersion.set(key, files);
    }
    return files;
}
function searchInVersion(engine, query, versionId) {
    const files = visibleFilesAt(versionId);
    return engine.search(query, (section) => files.has(section.file));
}
// Nulla vagy csak részleges (VAGY-) találatnál: melyik MÁS verzióban hány
// oldalon van minden szóra (ÉS-)találat — az index minden revíziót
// tartalmaz, ezért ez egyetlen további lekérdezés.
function otherVersionMatches(engine, query, excludedVersion) {
    const outcome = engine.search(query, () => true);
    if (outcome.pages.length === 0 || outcome.partial) {
        return [];
    }
    return state.versions
        .filter((version) => version.id !== excludedVersion)
        .map((version) => {
        const files = visibleFilesAt(version.id);
        const pages = outcome.pages.filter((hit) => hit.sections.some((sectionHit) => files.has(sectionHit.section.file))).length;
        return { version, pages };
    })
        .filter((entry) => entry.pages > 0)
        .slice(0, 5);
}
function appendSegments(parent, segments) {
    for (const segment of segments) {
        if (segment.hit) {
            const mark = document.createElement("mark");
            mark.textContent = segment.text;
            parent.append(mark);
        }
        else {
            parent.append(segment.text);
        }
    }
}
function createElement(tag, className, text) {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) {
        element.textContent = text;
    }
    return element;
}
// Egy találatra mutató link: a kattintást a handleSearchLinkClick() kezeli
// (az oldal helyben nyílik, a célszakaszhoz görgetve, kiemeléssel); a "href"
// a középső kattintásnak / új lapnak szól.
function createHitLink(className, versionId, pageId, anchor, terms) {
    const link = createElement("a", className);
    link.href = buildPageUrl(versionId, pageId, anchor);
    link.dataset.searchPage = pageId;
    link.dataset.searchAnchor = anchor;
    // A szavak nem tartalmazhatnak szóközt (a szóhatár éppen az), így
    // szóközzel elválasztva biztonságosan elférnek egy attribútumban.
    link.dataset.searchTerms = terms.join(" ");
    return link;
}
function createSearchPageLink(className, versionId, query, text) {
    const link = createElement("a", className, text);
    link.href = buildSearchUrl(versionId, query);
    link.dataset.searchQuery = query;
    link.dataset.searchVersion = versionId;
    return link;
}
function isSidebarSearchActive() {
    return state.search.trim() !== "";
}
// Keresés közben a fa helyén a találati lista látszik (a fa a háttérben
// változatlanul él tovább, a kinyitott ágakkal együtt).
function applySidebarMode() {
    const active = isSidebarSearchActive();
    contentsTree.hidden = active;
    searchResults.hidden = !active;
    if (active) {
        treeEmpty.hidden = true;
    }
}
function scheduleSidebarSearch(delay = searchDebounceMs) {
    window.clearTimeout(sidebarSearchTimer);
    if (!isSidebarSearchActive()) {
        searchResults.replaceChildren();
        renderTree();
        return;
    }
    applySidebarMode();
    sidebarSearchTimer = window.setTimeout(() => {
        void runSidebarSearch();
    }, delay);
}
function renderSidebarStatus(message, kind) {
    const status = createElement("p", kind === "error" ? "search-results__status search-results__status--error" : "search-results__status");
    if (kind === "loading") {
        const spinner = createElement("span", "spinner-border spinner-border-sm");
        spinner.setAttribute("aria-hidden", "true");
        status.append(spinner, " ");
    }
    status.append(message);
    searchResults.replaceChildren(status);
}
async function runSidebarSearch() {
    const query = state.search.trim().slice(0, maxQueryLength);
    const versionId = state.selectedVersion;
    const product = state.product;
    if (!query || !versionId || !product) {
        return;
    }
    if (!searchEngines.has(product)) {
        renderSidebarStatus("Keresési index betöltése…", "loading");
    }
    let engine;
    try {
        engine = await ensureSearchEngine();
    }
    catch {
        if (state.search.trim() === query) {
            renderSidebarStatus("A keresés most nem érhető el.", "error");
        }
        return;
    }
    if (state.search.trim().slice(0, maxQueryLength) !== query || state.product !== product || state.selectedVersion !== versionId) {
        return;
    }
    const outcome = searchInVersion(engine, query, versionId);
    renderSidebarResults(query, versionId, outcome);
    window.clearTimeout(searchTrackingTimer);
    searchTrackingTimer = window.setTimeout(() => {
        if (state.search.trim().slice(0, maxQueryLength) === query) {
            trackSearch(query, outcome, "sidebar");
        }
    }, searchTrackingDelayMs);
}
function renderSidebarHit(hit, versionId) {
    const best = hit.sections[0];
    const item = document.createElement("li");
    const link = createHitLink("search-result", versionId, hit.page.id, best?.section.anchor ?? "", hit.terms);
    if (hit.page.id === state.selectedPage) {
        link.classList.add("active");
        link.setAttribute("aria-current", "page");
    }
    link.append(createElement("span", "search-result__title", hit.page.title));
    if (hit.page.crumbs.length > 0) {
        link.append(createElement("span", "search-result__crumbs", hit.page.crumbs.join(crumbSeparator)));
    }
    if (best && best.section.heading !== "") {
        link.append(createElement("span", "search-result__section", best.section.heading));
    }
    if (best && best.section.text !== "") {
        const snippet = createElement("span", "search-result__snippet");
        appendSegments(snippet, buildSnippet(best.section.text, hit.terms, sidebarSnippetLength));
        link.append(snippet);
    }
    item.append(link);
    return item;
}
function renderSidebarResults(query, versionId, outcome) {
    const fragment = document.createDocumentFragment();
    const head = createElement("div", "search-results__head");
    const summary = outcome.queryTerms.length === 0
        ? "Legalább két karaktert írjon be."
        : outcome.pages.length === 0
            ? "Nincs találat."
            : `${outcome.pages.length} oldal`;
    head.append(createElement("span", "search-results__count", summary));
    if (outcome.queryTerms.length > 0) {
        head.append(createSearchPageLink("search-results__more", versionId, query, "Részletes találatok →"));
    }
    fragment.append(head);
    if (outcome.partial) {
        fragment.append(createElement("p", "search-results__note", "Nem minden szóra van találat."));
    }
    if (outcome.pages.length > 0) {
        const list = createElement("ul", "search-results__list");
        for (const hit of outcome.pages.slice(0, sidebarResultLimit)) {
            list.append(renderSidebarHit(hit, versionId));
        }
        fragment.append(list);
        if (outcome.pages.length > sidebarResultLimit) {
            const more = createElement("p", "search-results__footer");
            more.append(createSearchPageLink("search-results__more", versionId, query, `Mind a ${outcome.pages.length} találat →`));
            fragment.append(more);
        }
    }
    searchResults.replaceChildren(fragment);
    announce(outcome.pages.length === 0 ? summary : `${outcome.pages.length} oldalon van találat.`);
}
function searchPageTitle(query) {
    return `Keresés: „${query}”`;
}
function renderSearchPageHit(hit, versionId) {
    const item = createElement("li", "search-page__item");
    if (hit.page.crumbs.length > 0) {
        item.append(createElement("p", "search-page__crumbs", hit.page.crumbs.join(crumbSeparator)));
    }
    const title = createElement("h2", "search-page__title");
    const titleLink = createHitLink("search-page__title-link", versionId, hit.page.id, "", hit.terms);
    titleLink.textContent = hit.page.title;
    title.append(titleLink);
    item.append(title);
    const sections = createElement("ul", "search-page__sections");
    for (const sectionHit of hit.sections.slice(0, sectionsPerPageHit)) {
        const { section } = sectionHit;
        const entry = document.createElement("li");
        // A bevezetőnek (az első H2 előtti rész) nincs címe — ott a snippet maga
        // a találat, a címlink úgyis az oldal elejére visz.
        if (section.anchor !== "") {
            const link = createHitLink("search-page__section", versionId, hit.page.id, section.anchor, hit.terms);
            link.textContent = section.heading;
            entry.append(link);
        }
        if (section.text !== "") {
            const snippet = createElement("p", "search-page__snippet");
            appendSegments(snippet, buildSnippet(section.text, hit.terms, pageSnippetLength));
            entry.append(snippet);
        }
        if (entry.childNodes.length > 0) {
            sections.append(entry);
        }
    }
    if (sections.childElementCount > 0) {
        item.append(sections);
    }
    const hidden = hit.sections.length - sectionsPerPageHit;
    if (hidden > 0) {
        item.append(createElement("p", "search-page__more", `…és még ${hidden} szakasz ezen az oldalon.`));
    }
    return item;
}
function renderSearchPage(query, versionId, outcome, others) {
    const label = state.versions.find((version) => version.id === versionId)?.label ?? versionId;
    const container = createElement("div", "search-page");
    const summary = outcome.queryTerms.length === 0
        ? "Legalább két karakteres keresőszót írjon be."
        : outcome.pages.length === 0
            ? `Nincs találat a ${label} verzióban.`
            : `${outcome.pages.length} oldalon van találat a ${label} verzióban.`;
    container.append(createElement("p", "search-page__summary", summary));
    if (outcome.partial) {
        container.append(createElement("p", "search-page__note", "Nem minden szóra van találat — az alábbi oldalak a szavak egy részét tartalmazzák."));
    }
    else if (outcome.pages.length === 0 && outcome.queryTerms.length > 0) {
        container.append(createElement("p", "search-page__note", "Próbálja kevesebb vagy rövidebb szóval — az ékezet és a kis- és nagybetű nem számít."));
    }
    // A témának lehet, hogy csak egy másik programverzióban van (teljes)
    // leírása — lásd otherVersionMatches().
    if (others.length > 0) {
        container.append(createElement("p", "search-page__note", outcome.partial
            ? "Más programverzióban minden szóra van találat:"
            : "Más programverzióban van találat:"));
        const list = createElement("ul", "search-page__versions");
        for (const entry of others) {
            const item = document.createElement("li");
            item.append(createSearchPageLink("search-page__version", entry.version.id, query, `${entry.version.label} — ${entry.pages} oldal`));
            list.append(item);
        }
        container.append(list);
    }
    if (outcome.pages.length > 0) {
        const list = createElement("ol", "search-page__list");
        for (const hit of outcome.pages) {
            list.append(renderSearchPageHit(hit, versionId));
        }
        container.append(list);
    }
    // Ha a kereső nem talált (minden szóra), a kérdés az asszisztensnek is feltehető — az
    // nem szavakat, hanem a kérdés értelmét keresi, és összefoglalja a talált szakaszokat.
    if (assistant && outcome.queryTerms.length > 0 && (outcome.pages.length === 0 || outcome.partial)) {
        const askButton = createElement("button", "cs-btn cs-btn-secondary search-page__ask", "Kérdezze a súgó asszisztensét");
        askButton.setAttribute("type", "button");
        askButton.addEventListener("click", () => assistant?.open(query));
        container.append(askButton);
    }
    article.setAttribute("aria-busy", "false");
    releaseArticleHeight();
    article.replaceChildren(container);
    tocPanel.hidden = true;
    announce(summary);
}
// A részletes találati oldal a főhasábban (?version=…&q=…). A sidebar mezője
// ugyanazt a lekérdezést mutatja, így a két lista mindig egyezik.
async function showSearchPage(query, mode, controller) {
    const versionId = state.selectedVersion;
    const product = state.product;
    if (!versionId || !product) {
        return;
    }
    if (!controller) {
        requestController?.abort();
        requestController = new AbortController();
        controller = requestController;
    }
    state.selectedPage = undefined;
    state.searchQuery = query;
    updateActivePageInTree(undefined);
    updateRoute(mode);
    if (state.search !== query) {
        searchInput.value = query;
        state.search = query;
        clearSearchButton.hidden = false;
        scheduleSidebarSearch(0);
    }
    setArticleLoading();
    const title = searchPageTitle(query);
    document.title = `${title} | ${baseTitle()}`;
    setHeroTitle(title);
    try {
        const engine = await ensureSearchEngine();
        if (controller.signal.aborted || state.searchQuery !== query || state.product !== product || state.selectedVersion !== versionId) {
            return;
        }
        const outcome = searchInVersion(engine, query, versionId);
        const others = outcome.queryTerms.length > 0 && (outcome.pages.length === 0 || outcome.partial)
            ? otherVersionMatches(engine, query, versionId)
            : [];
        renderSearchPage(query, versionId, outcome, others);
        trackSearch(query, outcome, "page");
        if (mode === "push") {
            revealDocumentStart();
            article.focus({ preventScroll: true });
        }
    }
    catch {
        if (!controller.signal.aborted && state.searchQuery === query) {
            showArticleError("A keresés most nem érhető el.");
        }
    }
    finally {
        if (requestController === controller) {
            requestController = undefined;
        }
    }
}
// Találati linkekre kattintás (sidebar lista és találati oldal). true, ha
// kezelte. Módosítóbillentyűvel (új lap/ablak) a böngészőre bízzuk.
function handleSearchLinkClick(event) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return false;
    }
    const target = event.target;
    const queryLink = target.closest("a[data-search-query]");
    if (queryLink) {
        event.preventDefault();
        const query = queryLink.dataset.searchQuery ?? "";
        const versionId = queryLink.dataset.searchVersion;
        if (versionId && versionId !== state.selectedVersion) {
            void loadVersion(versionId, { query }, "push");
        }
        else {
            void showSearchPage(query, "push");
        }
        if (isDrawerLayout()) {
            closeDrawer(false);
        }
        return true;
    }
    const hitLink = target.closest("a[data-search-page]");
    const pageId = hitLink?.dataset.searchPage;
    if (hitLink && pageId) {
        event.preventDefault();
        void loadPage(pageId, "push", undefined, {
            anchor: hitLink.dataset.searchAnchor || undefined,
            highlight: (hitLink.dataset.searchTerms ?? "").split(" ").filter((term) => term !== ""),
        });
        if (isDrawerLayout()) {
            closeDrawer(false);
        }
        return true;
    }
    return false;
}
function readHighlightState() {
    const value = history.state;
    if (!isRecord(value) || !Array.isArray(value.searchHighlight)) {
        return undefined;
    }
    const terms = value.searchHighlight.filter((term) => typeof term === "string" && term !== "");
    return terms.length > 0 ? terms : undefined;
}
// A szavak teljes-szó előfordulásai <mark class="search-hit">-be kerülnek. A
// szövegcsomópontokat előbb összegyűjtjük, mert a csere közben a bejárás
// elveszítené a helyét. Visszaadja az első kiemelést (a görgetéshez).
function highlightSearchTerms(root, terms) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        nodes.push(node);
    }
    let first;
    for (const node of nodes) {
        if (node.parentElement?.closest("script, style, mark")) {
            continue;
        }
        const segments = splitByTerms(node.data, terms);
        if (!segments.some((segment) => segment.hit)) {
            continue;
        }
        const fragment = document.createDocumentFragment();
        for (const segment of segments) {
            if (segment.hit) {
                const mark = createElement("mark", "search-hit", segment.text);
                fragment.append(mark);
                if (!first) {
                    first = mark;
                }
            }
            else {
                fragment.append(segment.text);
            }
        }
        node.replaceWith(fragment);
    }
    return first;
}
// GA4 ajánlott "search" esemény — CSAK, ha a látogató elfogadta a statisztikai
// sütiket (különben a gtag sincs betöltve, és nem is töltődik be miattunk).
// A nulla találatos kifejezésekből derül ki, mi hiányzik a tartalomból.
function trackSearch(query, outcome, location) {
    if (outcome.queryTerms.length === 0 || !window.cobraAnalytics?.isEnabled() || !window.gtag) {
        return;
    }
    const key = `${location}\n${state.selectedVersion ?? ""}\n${query}`;
    if (key === lastTrackedSearch) {
        return;
    }
    lastTrackedSearch = key;
    window.gtag("event", "search", {
        search_term: query,
        results_count: outcome.pages.length,
        search_location: location,
        program_version: state.selectedVersion,
    });
}
function clearSidebarSearch() {
    searchInput.value = "";
    state.search = "";
    clearSearchButton.hidden = true;
    scheduleSidebarSearch();
}
function isEditableTarget(target) {
    return target instanceof HTMLElement
        && (target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT");
}
// A "/" billentyű és a mobil fejléc nagyító gombja: mobilon előbb kinyitja a
// sidebar fiókot (a mező ott van), és a fő menüt bezárja.
function focusSearch() {
    document.body.classList.remove("cs-nav-open");
    navToggle.setAttribute("aria-expanded", "false");
    if (isDrawerLayout() && !sidebar.classList.contains("open")) {
        openDrawer();
    }
    if (!searchInput.disabled) {
        searchInput.focus();
        searchInput.select();
    }
}
function openDrawer() {
    sidebar.classList.add("open");
    document.body.classList.add("drawer-open");
    drawerBackdrop.hidden = false;
    menuButton.setAttribute("aria-expanded", "true");
    menuButton.setAttribute("aria-label", "Tartalomjegyzék bezárása");
    menuCloseButton.focus();
}
function closeDrawer(returnFocus = true) {
    sidebar.classList.remove("open");
    document.body.classList.remove("drawer-open");
    drawerBackdrop.hidden = true;
    menuButton.setAttribute("aria-expanded", "false");
    menuButton.setAttribute("aria-label", "Tartalomjegyzék megnyitása");
    if (returnFocus) {
        menuButton.focus();
    }
}
function trapDrawerFocus(event) {
    if (event.key !== "Tab" || !sidebar.classList.contains("open")) {
        return;
    }
    const focusable = [...sidebar.querySelectorAll('button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
        return;
    }
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    }
    else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}
// Egy kinyitható menüpont ki/becsukása (a nyíl gombja és a menüpont címe is
// ezt hívja); true, ha kinyílt. A fa újrarajzolódik, ezért ha a kattintott
// elemen volt a fókusz (pl. billentyűzettel, Enterrel nyitották), az új fa
// ugyanazon sorának ugyanazon elemére kerül vissza.
function toggleTreeItem(key, source) {
    const isExpanded = !state.expanded.has(key);
    if (isExpanded) {
        state.expanded.add(key);
    }
    else {
        state.expanded.delete(key);
    }
    const hadFocus = document.activeElement === source;
    renderTree();
    if (hadFocus) {
        const toggle = contentsTree.querySelector(`button[data-tree-key="${CSS.escape(key)}"]`);
        const focusTarget = source instanceof HTMLButtonElement ? toggle : toggle?.parentElement?.querySelector("a");
        focusTarget?.focus();
    }
    return isExpanded;
}
contentsTree.addEventListener("click", (event) => {
    const target = event.target;
    const toggle = target.closest("button[data-tree-key]");
    if (toggle?.dataset.treeKey) {
        toggleTreeItem(toggle.dataset.treeKey, toggle);
        return;
    }
    const link = target.closest("a[data-page]");
    // Kinyitható menüpont címe (saját oldalas link vagy oldal nélküli
    // kategória): az első kattintás kinyitja és megnyitja a saját oldalát, ha
    // van; a következő becsukja, a főhasáb tartalma marad. A fiók mobilon nyitva
    // marad, hogy a kinyílt alpontok látsszanak.
    const rowKey = target.closest(".tree-row")?.querySelector("button[data-tree-key]")?.dataset.treeKey;
    const title = link ?? target.closest(".tree-label");
    if (rowKey && title) {
        event.preventDefault();
        const page = link?.dataset.page;
        if (toggleTreeItem(rowKey, title) && page && page !== state.selectedPage) {
            void loadPage(page, "push");
        }
        return;
    }
    if (link?.dataset.page) {
        event.preventDefault();
        void loadPage(link.dataset.page, "push");
        if (isDrawerLayout()) {
            closeDrawer(false);
        }
    }
});
searchResults.addEventListener("click", (event) => {
    handleSearchLinkClick(event);
});
article.addEventListener("click", (event) => {
    // A részletes találati oldal linkjei (lásd renderSearchPage()).
    if (handleSearchLinkClick(event)) {
        return;
    }
    const link = event.target.closest("a[data-doc-page]");
    if (link?.dataset.docPage) {
        event.preventDefault();
        void loadPage(link.dataset.docPage, "push", undefined, { anchor: link.dataset.docAnchor });
        return;
    }
    // Kinyitható szakaszcím (lásd buildDocumentAccordion()) — ugyanaz a
    // kattintás-kezelési minta, mint a www.cobra.hu GYIK oldalán (lásd
    // Web/Www/src/ts/gyik.ts setupToggle()).
    const toggle = event.target.closest(".doc-accordion__toggle");
    if (toggle) {
        const item = toggle.closest(".cs-faq-item");
        const body = item?.querySelector(".cs-faq-answer");
        if (!item || !body) {
            return;
        }
        const isOpen = !body.hidden;
        body.hidden = isOpen;
        toggle.setAttribute("aria-expanded", isOpen ? "false" : "true");
        item.classList.toggle("is-open", !isOpen);
        return;
    }
    handleInPageAnchorClick(event);
});
// A jobb oldali "Ezen az oldalon" tartalomjegyzék linkjei is a cikken belüli
// horgonyokra mutatnak — ugyanaz a kinyitás+görgetés kell nekik, mint a
// cikken belüli linkeknek (lásd fent).
pageToc.addEventListener("click", handleInPageAnchorClick);
// Termékváltáskor a termék legújabb verziója és a változásnaplója nyílik meg —
// ugyanúgy, mint verzióváltáskor.
productSelect.addEventListener("change", () => {
    void loadProduct(productSelect.value, {}, "push");
});
versionSelect.addEventListener("change", () => {
    // Verzióváltáskor a kiválasztott oldal marad (az új verzióban érvényes
    // revíziójával), ha ott is létezik; ha nem, vagy nem volt kiválasztott
    // oldal, az új verzió változásnaplója nyílik meg. A találati oldal ugyanazt
    // a keresést futtatja le az új verzióban.
    const versionId = versionSelect.value;
    const query = state.searchQuery;
    const page = state.selectedPage;
    const item = page ? state.pages.get(page) : undefined;
    const keepPage = item !== undefined && resolveTargetFile(item, versionId) !== undefined;
    void loadVersion(versionId, query !== undefined ? { query } : keepPage ? { page } : {}, "push");
});
searchInput.addEventListener("input", () => {
    state.search = searchInput.value;
    clearSearchButton.hidden = state.search === "";
    scheduleSidebarSearch();
});
// Az index letöltése már a mező fókuszára / fölé vitt egérre elindul, hogy
// az első leütésre (jó eséllyel) kész legyen.
searchInput.addEventListener("focus", prefetchSearchIndex);
searchInput.addEventListener("pointerenter", prefetchSearchIndex);
searchInput.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && searchInput.value !== "") {
        // Az első Esc csak a mezőt üríti, a fiókot (mobilon) a második zárja.
        event.stopPropagation();
        clearSidebarSearch();
    }
    else if (event.key === "Enter" && !event.isComposing) {
        event.preventDefault();
        const query = searchInput.value.trim().slice(0, maxQueryLength);
        if (query !== "" && state.selectedVersion) {
            void showSearchPage(query, "push");
            if (isDrawerLayout()) {
                closeDrawer(false);
            }
        }
    }
    else if (event.key === "ArrowDown" && !searchResults.hidden) {
        const first = searchResults.querySelector("a.search-result");
        if (first) {
            event.preventDefault();
            first.focus();
        }
    }
});
clearSearchButton.addEventListener("click", () => {
    clearSidebarSearch();
    searchInput.focus();
});
searchButton.addEventListener("click", focusSearch);
menuButton.addEventListener("click", openDrawer);
menuCloseButton.addEventListener("click", () => closeDrawer());
drawerBackdrop.addEventListener("click", () => closeDrawer());
// Fejléc fő-navigáció (site-nav) mobil ki/be kapcsolása — ugyanaz a
// viselkedés, mint a www.cobra.hu-n (lásd
// Web/Www/src/ts/nav.ts setupNavToggle()-je: a hamburger
// gomb a "cs-nav-open" body-osztályt kapcsolja, ami a design-system.css
// szerint 1024px alatt fedi/mutatja a "#navPanel"-t). A help.cobra.hu
// menüpontjai (renderSiteNav: a tartalomjegyzék legfelső szintje) mind lapos
// linkek (nincs almenü/gyerek), ezért csak a mobil nyitás/zárás logikát
// vesszük át innen, a legördülő almenü-
// kezelést (openDropdown/closeDropdown) nem — ha a jövőben csoportosított,
// almenüs menüpontok kellenének, azt is a nav.ts mintájára kell pótolni.
navToggle.addEventListener("click", () => {
    const isOpen = document.body.classList.toggle("cs-nav-open");
    navToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
});
// Ha a mobil panelen belül valódi linkre (vagy a "Tartalomjegyzék
// megnyitása" gombra) kattintanak, záródjon be a panel — ugyanaz a minta,
// mint a www.cobra.hu-n.
navPanel.addEventListener("click", (event) => {
    const target = event.target;
    const link = target.closest("a");
    if (!link && !target.closest("#menu-button")) {
        return;
    }
    // A fejléc oldallinkjei (data-page, lásd createMenuLink) helyben, a
    // kiválasztott verzióban nyílnak meg — ugyanúgy, mint a fa linkjei.
    const pageId = link?.dataset.page;
    if (pageId && state.selectedVersion && state.pages.has(pageId)) {
        event.preventDefault();
        void loadPage(pageId, "push");
    }
    document.body.classList.remove("cs-nav-open");
    navToggle.setAttribute("aria-expanded", "false");
});
document.addEventListener("keydown", (event) => {
    if (event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey && !isEditableTarget(event.target)) {
        event.preventDefault();
        focusSearch();
    }
    else if (event.key === "Escape" && sidebar.classList.contains("open")) {
        closeDrawer();
    }
    else if (event.key === "Escape" && document.body.classList.contains("cs-nav-open")) {
        document.body.classList.remove("cs-nav-open");
        navToggle.setAttribute("aria-expanded", "false");
    }
    else {
        trapDrawerFocus(event);
    }
});
window.addEventListener("resize", () => {
    if (!isDrawerLayout() && sidebar.classList.contains("open")) {
        closeDrawer(false);
    }
});
window.addEventListener("popstate", () => {
    const route = readRoute();
    const productId = route.product && state.products.some((product) => product.id === route.product)
        ? route.product
        : state.defaultProduct;
    if (productId && productId !== state.product) {
        void loadProduct(productId, route, "none");
        return;
    }
    if (!route.version) {
        requestController?.abort();
        state.selectedVersion = undefined;
        state.selectedPage = undefined;
        state.searchQuery = undefined;
        state.items = [];
        state.pages = new Map();
        searchInput.disabled = true;
        renderVersions();
        renderTree();
        renderSiteNav();
        showArticlePrompt("Válasszon programverziót.");
    }
    else if (route.version !== state.selectedVersion) {
        void loadVersion(route.version, { page: route.page, query: route.query }, "none");
    }
    else if (route.query !== undefined) {
        if (route.query !== state.searchQuery) {
            void showSearchPage(route.query, "none");
        }
    }
    else if (route.page && (route.page !== state.selectedPage || state.searchQuery !== undefined)) {
        void loadPage(route.page, "none");
    }
    else if (!route.page && (state.selectedPage || state.searchQuery !== undefined)) {
        requestController?.abort();
        state.selectedPage = undefined;
        state.searchQuery = undefined;
        renderTree();
        showArticlePrompt("Válasszon dokumentumot a tartalomjegyzékből.");
    }
});
// A görgetéskor a lap tetejére tapadó fejléc világos/sötét váltása — a
// www.cobra.hu-val közös, lásd Brand/Web/ts/header.ts.
setupHeaderScrollState();
// A súgó-asszisztens a kiválasztott termékről és verzióról, és az épp nyitott oldalról
// kérdez; a forrásai ugyanúgy nyílnak meg, mint egy menüpont (verzióváltással is). Csak
// akkor jelenik meg, ha a háttere elérhető — addig (és ha nem az) a keresési oldal
// „Kérdezze a súgó asszisztensét” gombja sem.
const assistantRoot = document.getElementById("assistant");
if (assistantRoot) {
    void setupAssistant(assistantRoot, {
        getContext() {
            const version = state.versions.find((entry) => entry.id === state.selectedVersion);
            if (!state.product || !version) {
                return undefined;
            }
            return {
                product: state.product,
                productLabel: productLabel(),
                version: version.id,
                versionLabel: version.label,
                page: state.searchQuery === undefined ? state.selectedPage : undefined,
            };
        },
        navigate({ version, page, anchor }) {
            if (version !== state.selectedVersion) {
                void loadVersion(version, { page, anchor }, "push");
            }
            else {
                void loadPage(page, "push", undefined, { anchor });
            }
        },
    }).then((controller) => {
        assistant = controller;
    });
}
async function start() {
    if (window.location.protocol === "file:") {
        productSelect.innerHTML = "<option>HTTP-kiszolgáló szükséges</option>";
        versionSelect.innerHTML = "<option>HTTP-kiszolgáló szükséges</option>";
        showArticleError("A dokumentáció helyi fájlként nem tölthető be. Indítsa el a help.cobra.hu könyvtárban az npm run dev parancsot, majd nyissa meg a megjelenő HTTP-címet.");
        return;
    }
    let productsDocument;
    try {
        productsDocument = validateProducts(parse(await fetchText("../content/products.yaml", undefined, "no-cache")));
    }
    catch (error) {
        console.error("Az alkalmazás indítása sikertelen:", error);
        productSelect.innerHTML = "<option>Nem érhető el</option>";
        versionSelect.innerHTML = "<option>Nem érhető el</option>";
        showArticleError("A dokumentáció terméklistája nem tölthető be.");
        return;
    }
    state.products = productsDocument.products;
    state.defaultProduct = productsDocument.defaultProduct;
    // A címsor nélküli (vagy ismeretlen) termék az alapértelmezett — így a
    // ?product= előtti linkek (?page=…, ?version=…) változatlanul működnek.
    const route = readRoute();
    const productId = route.product && state.products.some((product) => product.id === route.product)
        ? route.product
        : productsDocument.defaultProduct;
    state.product = productId;
    renderProducts();
    await loadProduct(productId, route, "replace");
}
void start();
